<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import FormButton from '../../Atoms/FormButton.vue';
import TabGroup from '../../Atoms/TabGroup.vue'
import TabItem from '../../Atoms/TabItem.vue'
import IconBase from '../../icon/IconBase.vue';
import IconCamera from '../../icon/IconCamera.vue';
import IconChevronUpDown from '../../icon/IconChevronUpDown.vue';
import IconMic from '../../icon/IconMic.vue';
import DropDownMenu from '../../Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../Atoms/DropDownMenuItem.vue';
import IconCheckmark from '../../icon/IconCheckmark.vue';

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  streamAlive: {
    type: Boolean
  },
  isRtmpPullEnabled: {
    type: Boolean,
    default: false
  },
  rmptPullUrlProcessing: {
    type: Boolean,
    default: false
  },
  transcoderView: {
    type: Boolean,
    default: false
  },
  streamSourceType: {
    type: String
  },
  sourceTypes: {
    type: Object
  },
  selectedLinkedSource: {
    type: Object,
    default: null
  },
  sourceSet: {
    type: Boolean,
    default: false
  }
})

const windowWidth = ref(window.innerWidth)
const webcamDevices = ref(null)
const audioDevice = ref(null)
const videoDevice = ref(null)
const audioDeviceDropdown = ref(null)
const cameraDeviceDropdown = ref(null)

const emit = defineEmits({
  'request-url': () => true,
  'set-linked-source': () => true,
  'init-selected-source': () => true,
  'selected-source': () => true,
  'set-audio-device': () => true,
  'set-video-device': () => true
})

const onWindowResize = () => {
  windowWidth.value = window.innerWidth
}

onMounted(async () => {
  window.addEventListener('resize', onWindowResize)
  webcamDevices.value = await navigator.mediaDevices?.enumerateDevices();
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize)
})

const isMobile = computed(() => {
  if (windowWidth.value > 767) {
    return false
  } else {
    return !props.transcoderView
  }
})

// The device pickers are hidden while the stream source is pull mode
const hasPullSource = computed(() => props.streamSourceType === props.sourceTypes?.Pull)

const audioDevices = computed(() => webcamDevices.value?.filter((device) => device.kind === 'audioinput'))
const videoDevices = computed(() => webcamDevices.value?.filter((device) => device.kind === 'videoinput'))
const defaultAudioDevice = computed(() => audioDevice.value || audioDevices.value.find((device) => device.deviceId === 'default') || audioDevices.value[0])
const defaultVideoDevice = computed(() => videoDevice.value || videoDevices.value[0])

const setDefaultAudioDevice = (device) => {
  audioDevice.value = device
  audioDeviceDropdown.value.toggleDropdown()
  emit('set-audio-device', device)
}
const setDefaultVideoDevice = (device) => {
  videoDevice.value = device
  cameraDeviceDropdown.value.toggleDropdown()
  emit('set-video-device', device)
}

const activeTab = ref('sound')

const setActivetab = (tabName) => activeTab.value = tabName


</script>
<template>
  <!-- Webcam source tabs -->
  <div class="flex flex-wrap w-full mt-3 mb-2" v-if="!isMobile">
    <TabGroup type="segmented" custom-classes="mb-3">
      <TabItem :is-active="activeTab === 'sound'" @click="setActivetab('sound')">Sound</TabItem>
      <TabItem :is-active="activeTab === 'camera'" @click="setActivetab('camera')">Camera</TabItem>
    </TabGroup>
    <div class="w-full bg-surface-1 rounded-lg p-3" v-if="audioDevices?.length && (!hasPullSource)">
      <section v-if="activeTab === 'sound'">
        <h6 class="text-sm text-surface-8 flex items-center gap-x-2 mb-1">
          <icon-base width="16px" height="16px" fill="transparent">
            <icon-mic />
          </icon-base>
          Input device
        </h6>
        <DropDownMenu type="secondary" :disabled="streamAlive" ref="audioDeviceDropdown" full-width wrapperClass="min-w-[240px]"
          maxHeight="64">
          <template #toggle-button>
            <span class="truncate">{{ defaultAudioDevice.label }}</span>
            <icon-base class="ml-auto">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <template #menu-items>
            <DropDownMenuItem v-for="(device, index) in audioDevices" :key="index"
              @click="setDefaultAudioDevice(device)" :disabled="streamAlive">
              <icon-base v-if="defaultAudioDevice.label === device.label" class="text-success">
                <icon-checkmark />
              </icon-base>
              <span>{{ device.label }}</span>
            </DropDownMenuItem>
          </template>
        </DropDownMenu>
      </section>
      <section v-if="activeTab === 'camera'">
        <h6 class="text-sm text-surface-8 flex items-center gap-x-2 mb-1">
          <icon-base>
            <icon-camera />
          </icon-base>
          Camera
        </h6>
        <DropDownMenu type="secondary" :disabled="streamAlive" ref="cameraDeviceDropdown" full-width wrapperClass="min-w-[240px]"
          maxHeight="64">
          <template #toggle-button>
            <span class="truncate">{{ defaultVideoDevice.label }}</span>
            <icon-base class="ml-auto">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <template #menu-items>
            <DropDownMenuItem v-for="(device, index) in videoDevices" :key="index"
              @click="setDefaultVideoDevice(device)" :disabled="streamAlive">
              <icon-base v-if="defaultVideoDevice.label === device.label" class="text-success">
                <icon-checkmark />
              </icon-base>
              <span>{{ device.label }}</span>
            </DropDownMenuItem>
          </template>
        </DropDownMenu>
      </section>
    </div>
  </div>
  <div class="flex w-full flex-col items-center" v-else>
    <div class="w-full text-center py-2">
      <img class="inline-block mx-auto mb-2" src="@/assets/images/no-access.svg" alt="No Access">
      <p class="text-s-ss text-dark-8 text-400">Webcam access feature <br>is not available on mobile.</p>
    </div>
  </div>
</template>